import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { fetchFeed } from "@/lib/posts";
import { PostCard, type FeedPost } from "@/components/post-card";
import { FeedSkeleton } from "@/components/feed-skeleton";
import { useAuth } from "@/lib/auth-context";
import { Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/bookmarks")({
  head: () => ({
    meta: [
      { title: "Xamseen — Bookmarks" },
      { name: "description", content: "Everything you saved on Xamseen — reports, discussions and solutions you want to come back to." },
      { property: "og:title", content: "Xamseen — Bookmarks" },
      { property: "og:description", content: "The reports and discussions you saved on Xamseen." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: BookmarksPage,
});

function BookmarksPage() {
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) { setPosts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await supabase
        .from("saves")
        .select("post_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(100);
      const ids = (data ?? []).map((r: any) => r.post_id).filter(Boolean);
      const rows = await fetchFeed({ ids, limit: 100 });
      const order = new Map(ids.map((id: string, i: number) => [id, i]));
      rows.sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
      setPosts(rows);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { if (!authLoading) void load(); }, [authLoading, load]);

  return (
    <AppShell>
      <div className="space-y-5">
        <header>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Bookmarks</h1>
          <p className="text-sm text-muted-foreground">Saved reports, discussions and solutions.</p>
        </header>

        {!user && !authLoading ? (
          <div className="rounded-2xl border border-border p-12 text-center">
            <p className="font-medium">Sign in to see your bookmarks</p>
            <Button asChild className="mt-4"><Link to="/auth">Sign in</Link></Button>
          </div>
        ) : loading || authLoading ? (
          <FeedSkeleton />
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-border p-12 text-center">
            <Bookmark className="mx-auto mb-3 h-9 w-9 text-muted-foreground" />
            <p className="font-medium">Nothing saved yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Tap the bookmark icon on any post to keep it here.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {posts.map((p) => <PostCard key={p.id} post={p} onChanged={load} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}
